import { type MouseEvent, useState } from 'react';
import Check from '@mui/icons-material/Check';
import ImportExportOutlined from '@mui/icons-material/ImportExportOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import {
  Button,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  type SituationSort,
  situationCategoryOptions,
  situationSortOptions,
} from '@/packages/academic-hub/constants';
import type { CourseTypes } from '@/models/catalog/course';

interface SituationFiltersProps {
  categories: CourseTypes[];
  sort: SituationSort;
  onCategoriesChange: (categories: CourseTypes[]) => void;
  onSortChange: (sort: SituationSort) => void;
}

export const SituationFilters = ({
  categories,
  sort,
  onCategoriesChange,
  onSortChange,
}: SituationFiltersProps) => {
  const [categoriesAnchor, setCategoriesAnchor] = useState<null | HTMLElement>(
    null
  );
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);

  const openCategories = (event: MouseEvent<HTMLButtonElement>) =>
    setCategoriesAnchor(event.currentTarget);
  const openSort = (event: MouseEvent<HTMLButtonElement>) =>
    setSortAnchor(event.currentTarget);

  const toggleCategory = (category: CourseTypes) => {
    onCategoriesChange(
      categories.includes(category)
        ? categories.filter((value) => value !== category)
        : [...categories, category]
    );
  };

  const selectSort = (value: SituationSort) => {
    onSortChange(value);
    setSortAnchor(null);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        position: { xs: 'static', sm: 'absolute' },
        right: { sm: 0 },
        bottom: { sm: 8 },
        justifyContent: { xs: 'flex-end', sm: 'initial' },
        flexWrap: 'wrap',
        mt: { xs: 2, sm: 0 },
        backgroundColor: 'background.default',
      }}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<SchoolOutlined />}
        endIcon={<KeyboardArrowDown />}
        onClick={openCategories}
      >
        Categorias
      </Button>
      <Menu
        anchorEl={categoriesAnchor}
        open={Boolean(categoriesAnchor)}
        onClose={() => setCategoriesAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {situationCategoryOptions.map(({ value, label }) => (
          <MenuItem key={value} onClick={() => toggleCategory(value)} dense>
            <Checkbox
              edge="start"
              size="small"
              checked={categories.includes(value)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Button
        variant="outlined"
        size="small"
        startIcon={<ImportExportOutlined />}
        endIcon={<KeyboardArrowDown />}
        onClick={openSort}
      >
        Ordenar por
      </Button>
      <Menu
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {situationSortOptions.map(({ value, label }) => (
          <MenuItem key={value} onClick={() => selectSort(value)} dense>
            <ListItemIcon>
              {sort === value && <Check fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
